import type { SubmitPurchaseTrackingContext } from '@lingoda/api';
import { getConsentSnapshot } from '@lingoda/consent';
import { getCookieValue } from '@lingoda/cookie';
import { getStorageValue, setStorageValue } from '@lingoda/utils';

// "GA1.1.123456789.987654321" -> "123456789.987654321" (version + domain-depth
// prefix stripped; the remainder is the GA4 client id)
const extractGaClientId = (gaCookie: string | undefined): string | undefined => {
    if (!gaCookie) {
        return undefined;
    }

    const parts = gaCookie.split('.');

    if (parts.length < 4) {
        return undefined;
    }

    return parts.slice(2).join('.');
};

/**
 * Attribution/consent values read in the browser at checkout submit. The backend keeps
 * them as the marker of a tracking-aware checkout, so nothing else may send them.
 */
export const buildTrackingContext = (): SubmitPurchaseTrackingContext => {
    const consent = getConsentSnapshot();
    // A withdrawn consent does not always take the cookie with it, so each identifier
    // is gated by the signal that covers it, the same way the backend gates storing it.
    const adConsented = consent.consentAdStorage === true;
    const analyticsConsented = consent.consentAnalyticsStorage === true;

    return {
        gaClientId: analyticsConsented ? extractGaClientId(getCookieValue('_ga')) : undefined,
        fbp: adConsented ? getCookieValue('_fbp') : undefined,
        fbc: adConsented ? getCookieValue('_fbc') : undefined,
        consentAdStorage: consent.consentAdStorage ?? undefined,
        consentAnalyticsStorage: consent.consentAnalyticsStorage ?? undefined,
        consentAdUserData: consent.consentAdUserData ?? undefined,
        consentAdPersonalization: consent.consentAdPersonalization ?? undefined,
    };
};

const trackingContextSentStorageKey = (purchaseId: string) => `tracking_ctx_sent_${purchaseId}`;

// Pairs the submit with the dataLayer event fired later, on another page: only a
// checkout that stored its context on the backend may report a trial as trial_started.
export const markTrackingContextSent = (purchaseId: string) =>
    setStorageValue(trackingContextSentStorageKey(purchaseId), true);

export const wasTrackingContextSent = (purchaseId: string) =>
    getStorageValue<boolean>(trackingContextSentStorageKey(purchaseId)) === true;
